import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListingContext } from '../../../contexts/ListingContext';
import styles from '../../../styles/ListingManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import type {
  ListingType,
  ListingStatus,
  ListingTypeOption,
  ListingClientType,
  ListingDocumentType,
  ListingImageType,
} from '../../../types/ListingTypes';
import AddClient, { type StagedClient } from './AddClient';
import ClientList from './ClientList';

interface AddListingProps {
  userDetails: any;
}

interface PropertyForm {
  listingType: ListingType | '';
  complexName: string;
  unitNumber: string;
  streetNumber: string;
  streetName: string;
  city: string;
  province: string;
  postalCode: string;
  status: ListingStatus;
}

const INITIAL_FORM: PropertyForm = {
  listingType: '',
  complexName: '',
  unitNumber: '',
  streetNumber: '',
  streetName: '',
  city: '',
  province: '',
  postalCode: '',
  status: 'Open',
};

const PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape',
];

const IMAGE_TYPES: ListingImageType[] = [
  'Bedroom', 'Bathroom', 'Kitchen', 'Patio', 'Garden', 'Front', 'Back', 'Scullery', 'Garage',
];

const AddListing: React.FC<AddListingProps> = ({ userDetails }) => {
  const navigate = useNavigate();
  const {
    createListing,
    createClient,
    uploadClientDocument,
    uploadListingDocument,
    uploadListingImage,
    fetchListingTypes,
    fetchClientTypes,
    fetchDocumentTypes,
    loading,
    error: contextError,
  } = useListingContext();

  const [form, setForm] = useState<PropertyForm>(INITIAL_FORM);
  const [listingTypeOptions, setListingTypeOptions] = useState<ListingTypeOption[]>([]);
  const [clientTypes, setClientTypes] = useState<ListingClientType[]>([]);
  const [listingDocumentTypes, setListingDocumentTypes] = useState<ListingDocumentType[]>([]);
  const [stagedListingDocs, setStagedListingDocs] = useState<Record<string, File>>({});
  const [stagedImages, setStagedImages] = useState<{ tag: ListingImageType; file: File }[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageTag, setImageTag] = useState<ListingImageType>('Front');
  const [stagedClients, setStagedClients] = useState<StagedClient[]>([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch listing type options once on mount
  useEffect(() => {
    fetchListingTypes().then(setListingTypeOptions).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When listing type changes, load its client types and document types
  useEffect(() => {
    if (!form.listingType) {
      setClientTypes([]);
      setListingDocumentTypes([]);
      setStagedListingDocs({});
      setStagedClients([]);
      return;
    }
    const typeOption = listingTypeOptions.find(t => t.name === form.listingType);
    if (!typeOption) return;

    fetchClientTypes({ listingTypeId: typeOption.id }).then(setClientTypes).catch(() => {});
    fetchDocumentTypes(typeOption.id).then(setListingDocumentTypes).catch(() => {});
    setStagedListingDocs({});
    setStagedClients([])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.listingType, listingTypeOptions]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddClient = (client: StagedClient) => {
    setStagedClients(prev => [...prev, client]);
    setShowAddClient(false);
  };

  const handleRemoveClient = (tempId: string) => {
    setStagedClients(prev => prev.filter(c => c.tempId !== tempId));
  };

  const handleAddImage = () => {
    if (!imageFile) return;
    setStagedImages(prev => [...prev, { tag: imageTag, file: imageFile }]);
    setImageFile(null);
  };

  const handleRemoveImage = (index: number) => {
    setStagedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleListingDocFile = (docTypeId: string, file: File | null) => {
    setStagedListingDocs(prev => {
      if (!file) {
        const { [docTypeId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [docTypeId]: file };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.listingType) { setError('Please select a listing type.'); return; }
    if (!form.streetNumber || isNaN(Number(form.streetNumber))) { setError('Street number must be a valid number.'); return; }
    if (!form.streetName.trim()) { setError('Street name is required.'); return; }
    if (!form.city.trim()) { setError('City is required.'); return; }
    if (!form.province) { setError('Province is required.'); return; }
    if (!form.postalCode.trim()) { setError('Postal code is required.'); return; }

    setSubmitting(true);
    try {
      // 1. Create the listing
      const created = await createListing({
        listingType: form.listingType as ListingType,
        complexName: form.complexName.trim() || undefined,
        unitNumber: form.unitNumber ? Number(form.unitNumber) : undefined,
        streetNumber: Number(form.streetNumber),
        streetName: form.streetName.trim(),
        city: form.city.trim(),
        province: form.province,
        postalCode: form.postalCode.trim(),
        status: form.status,
        userId: userDetails?.id,
        agencyId: userDetails?.agency?.agencyId || undefined,
      });

      // 2. Create each staged client and upload their documents
      for (const client of stagedClients) {
        const created_client = await createClient({
          listingId: created.id,
          name: client.name,
          surname: client.surname,
          email: client.email,
          cell: client.cell,
          streetNumber: Number(client.streetNumber),
          streetName: client.streetName,
          city: client.city,
          province: client.province,
          postalCode: client.postalCode,
          maritalStatus: client.maritalStatus,
          clientType: client.clientTypeId,
        });

        // Upload each staged document for this client
        for (const doc of client.documents) {
          await uploadClientDocument(
            created_client.id,
            doc.documentTypeId,
            doc.documentTypeName,
            doc.file,
          );
        }
      }

      // 3. Upload staged listing-level documents
      for (const [docTypeId, file] of Object.entries(stagedListingDocs)) {
        const docType = listingDocumentTypes.find(dt => dt.id === docTypeId);
        await uploadListingDocument(
          created.id,
          docTypeId,
          docType?.name ?? file.name,
          file,
        );
      }

      // 4. Upload staged listing images
      for (const img of stagedImages) {
        await uploadListingImage(created.id, img.tag, img.file);
      }

      navigate(`/ListingViewer/${created.id}`);
    } catch {
      setError(contextError || 'Failed to create listing. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const textField = (label: string, name: keyof PropertyForm, type: 'text' | 'number' = 'text', required = true) => (
    <div className={styles.categoryRow}>
      <label className={styles.categoryLabel}>
        {label}{required && <span style={{ color: '#dc3545', marginLeft: 2 }}>*</span>}
      </label>
      <div className={styles.categoryInputWrapper}>
        <input
          className={styles.categoryInput}
          type={type}
          name={name}
          value={form[name] as string}
          onChange={handleChange}
          required={required}
          min={type === 'number' ? 0 : undefined}
        />
      </div>
    </div>
  );

  const isBusy = loading || submitting;

  return (
    <form className={styles.agreementForm} onSubmit={handleSubmit} noValidate>
      <h2 className={styles.agreementPageTitle}>Add Listing</h2>

      {/* ── Listing Type ── */}
      <section className={`${styles.agreementFormSection} ${styles.agreementTypeSection}`}>
        <span className={styles.agreementTypeLabel}>Listing Type *</span>
        <select
          className={styles.agreementTypeSelect}
          name="listingType"
          value={form.listingType}
          onChange={handleChange}
          required
        >
          <option value="">— Select type —</option>
          <option value="Sale">Sale</option>
          <option value="Rental">Rental</option>
        </select>
      </section>

      {/* ── Property Details ── */}
      <section className={styles.agreementFormSection}>
        <h4>Property Details</h4>
        {textField('Complex Name', 'complexName', 'text', false)}
        {textField('Unit Number', 'unitNumber', 'number', false)}
        {textField('Street Number', 'streetNumber', 'number')}
        {textField('Street Name', 'streetName')}
        {textField('City', 'city')}

        <div className={styles.categoryRow}>
          <label className={styles.categoryLabel}>
            Province <span style={{ color: '#dc3545', marginLeft: 2 }}>*</span>
          </label>
          <div className={styles.categoryInputWrapper}>
            <select
              className={styles.categoryInput}
              name="province"
              value={form.province}
              onChange={handleChange}
              required
            >
              <option value="">— Select province —</option>
              {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        {textField('Postal Code', 'postalCode')}
      </section>

      {/* ── Status ── */}
      <section className={styles.agreementFormSection}>
        <h4>Status</h4>
        <div className={styles.categoryRow}>
          <label className={styles.categoryLabel}>Status</label>
          <div className={styles.categoryInputWrapper}>
            <select
              className={styles.categoryInput}
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <option value="Open">Open</option>
              <option value="Pending">Pending</option>
              <option value="Incomplete">Incomplete</option>
            </select>
          </div>
        </div>
      </section>

      {/* ── Listing Documents ── */}
      {form.listingType && listingDocumentTypes.length > 0 && (
        <section className={styles.agreementFormSection}>
          <h4>Listing Documents</h4>
          <p style={{ color: '#6c757d', marginBottom: 12, fontSize: '0.9em' }}>
            Upload the required documents for this listing.
          </p>
          {listingDocumentTypes.map(dt => {
            const file = stagedListingDocs[dt.id];
            return (
              <div key={dt.id} className={file ? styles.docCardWithDoc : styles.docCard}>
                <div className={styles.docCardRow}>
                  <div className={styles.docCardInfo}>
                    <span className={styles.documentLabel}>{dt.name}</span>
                    {file ? (
                      <div className={styles.docCardFileInfo}>
                        <span className={styles.docCardFileName}>{file.name}</span>
                        <span className={styles.docCardFileSize}>{(file.size / 1024).toFixed(1)} KB</span>
                        <span className={styles.docCardReady}>Ready to upload</span>
                      </div>
                    ) : (
                      <div className={styles.docUploadRow}>
                        <input
                          type="file"
                          onChange={e => handleListingDocFile(dt.id, e.target.files?.[0] ?? null)}
                        />
                      </div>
                    )}
                  </div>
                  {file && (
                    <button
                      type="button"
                      className={`${buttonStyles.button} ${buttonStyles.docRemove}`}
                      onClick={() => handleListingDocFile(dt.id, null)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* ── Listing Images ── */}
      <section className={styles.agreementFormSection}>
        <h4>Listing Images</h4>
        <p style={{ color: '#6c757d', marginBottom: 12, fontSize: '0.9em' }}>
          Upload photos of the property. Select a tag for each image before adding.
        </p>

        {/* Staged images list */}
        {stagedImages.length > 0 && (
          <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {stagedImages.map((img, i) => (
              <div key={i} className={styles.docCard}>
                <div className={styles.docCardRow}>
                  <div className={styles.docCardInfo}>
                    <span className={styles.documentLabel}>{img.tag}</span>
                    <div className={styles.docCardFileInfo}>
                      <span className={styles.docCardFileName}>{img.file.name}</span>
                      <span className={styles.docCardFileSize}>{(img.file.size / 1024).toFixed(1)} KB</span>
                      <span className={styles.docCardReady}>Ready to upload</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`${buttonStyles.button} ${buttonStyles.docRemove}`}
                    onClick={() => handleRemoveImage(i)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add image row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 0' }}>
          <select
            className={styles.categoryInput}
            style={{ width: 'auto' }}
            value={imageTag}
            onChange={e => setImageTag(e.target.value as ListingImageType)}
          >
            {IMAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input
            type="file"
            accept="image/*"
            onChange={e => setImageFile(e.target.files?.[0] ?? null)}
            key={stagedImages.length} /* reset input after add */
          />
          <button
            type="button"
            className={`${buttonStyles.button} ${buttonStyles.secondary}`}
            onClick={handleAddImage}
            disabled={!imageFile}
          >
            + Add Image
          </button>
        </div>
      </section>

      {/* ── Clients ── */}
      {form.listingType && (
        <section className={`${styles.agreementFormSection} ${styles.clientManagementSection}`}>
          <h4>Clients</h4>
          <p style={{ color: '#6c757d', marginBottom: 12, fontSize: '0.9em' }}>
            {form.listingType === 'Sale'
              ? 'Add the Buyer(s) and Seller(s) for this sale listing.'
              : 'Add the Leaser(s) and Lessee(s) for this rental listing.'}
          </p>

          <ClientList clients={stagedClients} onRemove={handleRemoveClient} />

          {showAddClient ? (
            <AddClient
              clientTypes={clientTypes}
              onAdd={handleAddClient}
              onCancel={() => setShowAddClient(false)}
            />
          ) : (
            <button
              type="button"
              className={`${buttonStyles.button} ${buttonStyles.secondary}`}
              style={{ marginTop: 12 }}
              onClick={() => setShowAddClient(true)}
            >
              + Add Client
            </button>
          )}
        </section>
      )}

      {/* ── Error & Actions ── */}
      {error && <p className={styles.agreementFormError}>{error}</p>}

      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <button
          type="submit"
          className={`${buttonStyles.button} ${buttonStyles.primary}`}
          disabled={isBusy}
        >
          {isBusy ? 'Creating...' : 'Create Listing'}
        </button>
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.secondary}`}
          onClick={() => navigate(-1)}
          disabled={isBusy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default AddListing;

